// ─────────────────────────────────────────────────────────────────────────────
// TraderIdentityCard
//
// Sits between BehavioralCostHero and the tab nav on the Analytics page.
// Synthesizes data already in scope (analytics, allDetectorResults, trades)
// into a single identity statement the trader can own.
//
// Cannot exist without trade history — everything here is derived from real
// behavior, not configuration.
//
// Data contracts (verified against src/lib/analytics.js):
//   analytics.winRate         → decimal  (0.0–1.0)
//   analytics.profitFactor    → number
//   analytics.avgWin          → positive dollar number
//   analytics.avgLoss         → positive dollar number (Math.abs applied)
//   analytics.total           → integer trade count
//   analytics.setupData[n].winRate → INTEGER (0–100), not decimal
//   analytics.setupData[n].setup   → string name
//   analytics.setupData[n].pnl     → number
//   analytics.setupData[n].total   → integer
//
// No avgHoldMinutes exists in analytics — scalper uses trade volume proxy.
// ─────────────────────────────────────────────────────────────────────────────

import { useMemo } from "react";
import { C } from "../../lib/colors";
import { fmt$, fmtPct } from "../../lib/format";

// ─── Trader type classification ───────────────────────────────────────────────
// Rules ordered — first match wins.
// analytics.winRate is decimal; setupData[n].winRate is integer.

const TRADER_TYPES = [
  {
    id:    "elite",
    label: "Elite Disciplined Trader",
    icon:  "🏆",
    color: "#ffd700",
    test:  (a) => a.winRate >= 0.75 && a.profitFactor >= 3 && a.total >= 30,
    desc:  (a) => `Top-tier execution across ${a.total} trades — ${fmtPct(a.winRate)} win rate and ${a.profitFactor.toFixed(1)}x profit factor. Your edge is statistically robust.`,
  },
  {
    id:    "precision",
    label: "Precision Trend Follower",
    icon:  "🎯",
    color: C.green,
    // setupData.winRate is integer (0-100)
    test:  (a, setups) => a.winRate >= 0.65 && topSetupIsDirectional(setups),
    desc:  (a) => `High win rate (${fmtPct(a.winRate)}) built on patient, directional setups. You wait for confluence before committing.`,
  },
  {
    id:    "scalper",
    label: "High-Frequency Scalper",
    icon:  "⚡",
    color: C.blue,
    // Proxy: 50+ trades is the volume signal for high-frequency style
    test:  (a) => a.total >= 50 && a.winRate >= 0.55,
    desc:  (a) => `Volume-based edge across ${a.total} trades. Quick execution and tight risk control are your weapons — ${fmtPct(a.winRate)} win rate validates the approach.`,
  },
  {
    id:    "momentum",
    label: "Momentum Trader",
    icon:  "🚀",
    color: C.purple,
    test:  (a, setups) => a.winRate >= 0.55 && a.profitFactor >= 2 && hasMomentumSetup(setups),
    desc:  (a) => `You ride moves with conviction — avg win ${fmt$(a.avgWin)} vs avg loss ${fmt$(a.avgLoss)} shows strong reward discipline.`,
  },
  {
    id:    "developing",
    label: "Developing Edge",
    icon:  "📈",
    color: C.yellow,
    test:  (a) => a.winRate >= 0.45 && a.total >= 20,
    desc:  (a) => `Positive expectancy building across ${a.total} trades. Patterns are emerging — keep logging and they'll sharpen.`,
  },
  {
    id:    "early",
    label: "Building Your Story",
    icon:  "🌱",
    color: C.muted,
    test:  () => true,
    desc:  (a) => `${a.total} trade${a.total !== 1 ? "s" : ""} logged. Your identity emerges from the data you create — keep going.`,
  },
];

function topSetupIsDirectional(setups) {
  if (!setups?.length) return false;
  const sorted = [...setups].sort((a, b) => b.winRate - a.winRate);
  const top = sorted[0]?.setup?.toLowerCase() || "";
  return top.includes("trend") || top.includes("breakout") ||
         top.includes("momentum") || top.includes("continuation") ||
         top.includes("vwap") || top.includes("reclaim");
}

function hasMomentumSetup(setups) {
  if (!setups?.length) return false;
  return setups.some(s => {
    const name = (s.setup || "").toLowerCase();
    return name.includes("momentum") || name.includes("breakout") ||
           name.includes("trend") || name.includes("continuation");
  });
}

function classifyTrader(analytics, setupData) {
  for (const type of TRADER_TYPES) {
    if (type.test(analytics, setupData)) return type;
  }
  return TRADER_TYPES[TRADER_TYPES.length - 1];
}

// ─── Edge score (0–100) ───────────────────────────────────────────────────────
// Win rate (30pts) + Profit factor (25pts) + Sample size (20pts) + Clean patterns (25pts)

function computeEdgeScore(analytics, allDetectorResults) {
  const wr = analytics.winRate ?? 0;
  const pf = analytics.profitFactor ?? 1;
  const n  = analytics.total ?? 0;
  const totalCost = getTotalMonthlyCost(allDetectorResults);

  const wrPts      = Math.min(30, Math.max(0, ((wr - 0.4) / 0.5) * 30));
  const pfPts      = Math.min(25, Math.max(0, ((pf - 1) / 4) * 25));
  const nPts       = Math.min(20, (n / 100) * 20);
  const costPenalty = Math.min(25, (totalCost / 200) * 25);
  const cleanPts   = 25 - costPenalty;

  const score = Math.round(Math.min(100, Math.max(0, wrPts + pfPts + nPts + cleanPts)));

  const breakdown = [
    {
      label: "Win Rate",
      pts:   wrPts,
      max:   30,
      tip:   `Your win rate is ${Math.round(wr * 100)}%. Scoring improves above 65%.`,
    },
    {
      label: "Profit Factor",
      pts:   pfPts,
      max:   25,
      tip:   `Profit factor ${pf.toFixed(2)}. Score maxes out at 5.0x.`,
    },
    {
      label: "Sample Size",
      pts:   nPts,
      max:   20,
      tip:   `${n} trades logged. Full points at 100+ trades.`,
    },
    {
      label: "Clean Patterns",
      pts:   cleanPts,
      max:   25,
      tip:   totalCost > 0
        ? `Behavioral patterns cost est. ${fmt$(-totalCost)}/mo. Fix leaks to recover these points.`
        : "No costly behavioral patterns detected.",
    },
  ];

  // One actionable guidance line — targets the lowest-scoring component
  const sorted = [...breakdown].sort((a, b) => (a.pts / a.max) - (b.pts / b.max));
  const weakest = sorted[0];
  let guidance = null;
  if (weakest.label === "Win Rate" && wrPts < 18) {
    guidance = `Win rate (${Math.round(wr * 100)}%) is your biggest drag. Focus on setup quality over trade frequency.`;
  } else if (weakest.label === "Profit Factor" && pfPts < 12) {
    guidance = `Profit factor ${pf.toFixed(2)} is below target. Let winners run further or cut losers tighter.`;
  } else if (weakest.label === "Sample Size" && nPts < 15) {
    guidance = `${n} trades logged — more data unlocks higher confidence in all metrics.`;
  } else if (weakest.label === "Clean Patterns" && cleanPts < 18) {
    guidance = `Behavioral patterns are your biggest leak. Check the Risk Discipline and Behavior tabs.`;
  }

  return { score, breakdown, guidance };
}

function getTotalMonthlyCost(allDetectorResults) {
  if (!allDetectorResults) return 0;
  return ["behavior", "risk", "psychology", "context"].reduce((sum, k) => {
    return sum + (allDetectorResults[k]?.total_monthly_cost ?? 0);
  }, 0);
}

function edgeScoreColor(score) {
  if (score >= 75) return C.green;
  if (score >= 50) return C.yellow;
  return C.red;
}

function edgeScoreLabel(score) {
  if (score >= 80) return "Strong Edge";
  if (score >= 65) return "Developing Edge";
  if (score >= 50) return "Mixed Signals";
  return "Needs Work";
}

// ─── Biggest leak ─────────────────────────────────────────────────────────────

function getBiggestLeak(allDetectorResults) {
  if (!allDetectorResults) return null;
  let worst = null;
  let worstCost = 0;
  for (const suiteKey of ["behavior", "risk", "psychology", "context"]) {
    const suite = allDetectorResults[suiteKey];
    if (!suite?.detectors) continue;
    for (const det of suite.detectors) {
      const cost = det.monthly_cost ?? det.estimated_monthly_cost ?? 0;
      if (cost > worstCost) {
        worstCost = cost;
        worst = { name: det.label || det.id, cost };
      }
    }
  }
  return worst && worstCost > 0 ? worst : null;
}

// ─── Best time window ─────────────────────────────────────────────────────────

function getBestTimeWindow(trades) {
  if (!trades?.length) return null;
  const windows = {
    "Pre-10am":  { wins: 0, total: 0 },
    "10am–12pm": { wins: 0, total: 0 },
    "12pm–2pm":  { wins: 0, total: 0 },
    "2pm–4pm":   { wins: 0, total: 0 },
  };
  for (const t of trades) {
    const time = t.entry_time || t.trade_time;
    if (!time) continue;
    const hour = parseInt(time.slice(0, 2), 10);
    const key = hour < 10 ? "Pre-10am"
              : hour < 12 ? "10am–12pm"
              : hour < 14 ? "12pm–2pm"
              : "2pm–4pm";
    windows[key].total++;
    if ((t.pnl ?? 0) > 0) windows[key].wins++;
  }
  let best = null, bestWR = 0;
  for (const [label, data] of Object.entries(windows)) {
    if (data.total < 5) continue;
    const wr = data.wins / data.total;
    if (wr > bestWR) { bestWR = wr; best = { label, winRate: wr, n: data.total }; }
  }
  return best;
}

// ─── Main component ───────────────────────────────────────────────────────────

export function TraderIdentityCard({ analytics, allDetectorResults, trades, pnlMode }) {
  const result = useMemo(() => {
    if (!analytics || analytics.total < 10) return null;

    const setupData = analytics.setupData || [];
    const traderType  = classifyTrader(analytics, setupData);
    const { score: edgeScore, breakdown: scoreBreakdown, guidance: scoreGuidance } = computeEdgeScore(analytics, allDetectorResults);
    const biggestLeak = getBiggestLeak(allDetectorResults);
    const bestTime    = getBestTimeWindow(trades);

    const sortedSetups = [...setupData].sort((a, b) => b.winRate - a.winRate);
    const bestSetup = sortedSetups[0];

    return { traderType, edgeScore, scoreBreakdown, scoreGuidance, biggestLeak, bestTime, bestSetup };
  }, [analytics, allDetectorResults, trades, pnlMode]);

  if (!result) return null;

  const { traderType, edgeScore, biggestLeak, bestTime, bestSetup } = result;
  const scoreColor = edgeScoreColor(edgeScore);

  // Edge sentence — setupData.winRate is integer so append "%" directly
  const edgeParts = [];
  if (bestSetup) edgeParts.push(`${bestSetup.setup} (${bestSetup.winRate}% WR)`);
  if (bestTime)  edgeParts.push(`${bestTime.label} (${Math.round(bestTime.winRate * 100)}% WR)`);
  const edgeSentence = edgeParts.length
    ? `Your clearest edge: ${edgeParts.join(" · ")}.`
    : null;

  return (
    <div style={{
      marginBottom: 16,
      background: "linear-gradient(135deg, #0f1420 0%, #141926 100%)",
      border: `1px solid ${traderType.color}30`,
      borderLeft: `3px solid ${traderType.color}`,
      borderRadius: 12,
      padding: "18px 20px",
      display: "flex",
      gap: 20,
      alignItems: "flex-start",
      flexWrap: "wrap",
    }}>
      {/* Left: identity */}
      <div style={{ flex: "1 1 280px", minWidth: 240 }}>
        <div style={{
          fontSize: 10, color: C.muted,
          textTransform: "uppercase", letterSpacing: "0.1em",
          fontWeight: 700, marginBottom: 6,
        }}>
          Your Trading Identity · {analytics.total} trades
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <span style={{ fontSize: 22 }}>{traderType.icon}</span>
          <span style={{
            fontSize: 18, fontWeight: 800,
            color: traderType.color, letterSpacing: "-0.01em",
          }}>
            {traderType.label}
          </span>
        </div>

        <div style={{ fontSize: 13, color: C.text, lineHeight: 1.6, marginBottom: 10 }}>
          {traderType.desc(analytics)}
        </div>

        {edgeSentence && (
          <div style={{ fontSize: 12, color: C.sub, lineHeight: 1.5, marginBottom: 10 }}>
            {edgeSentence}
          </div>
        )}

        {biggestLeak ? (
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "4px 10px",
            background: C.red + "12", border: `1px solid ${C.red}25`,
            borderRadius: 6, fontSize: 11, color: C.muted,
          }}>
            <span style={{ color: C.red }}>⚠</span>
            Biggest leak:{" "}
            <span style={{ color: C.red, fontWeight: 700 }}>{biggestLeak.name}</span>
            <span style={{ color: C.red }}>({fmt$(-biggestLeak.cost)}/mo)</span>
          </div>
        ) : (
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "4px 10px",
            background: C.green + "10", border: `1px solid ${C.green}25`,
            borderRadius: 6, fontSize: 11, color: C.green,
          }}>
            ✓ No costly behavioral patterns detected
          </div>
        )}
      </div>

      {/* Right: edge score + breakdown */}
      <div style={{
        flexShrink: 0,
        padding: "14px 18px",
        background: "#0b0d19",
        border: `1px solid ${C.border}`,
        borderRadius: 10,
        minWidth: 200,
      }}>
        {/* Score header */}
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>
              Edge Score
            </div>
            <div style={{
              fontSize: 42, fontWeight: 900, color: scoreColor,
              lineHeight: 1, fontFamily: "monospace", fontVariantNumeric: "tabular-nums",
            }}>
              {edgeScore}
            </div>
            <div style={{ fontSize: 10, color: scoreColor, fontWeight: 700, marginTop: 3, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              {edgeScoreLabel(edgeScore)}
            </div>
          </div>
        </div>

        {/* Score breakdown — shows what's driving the number */}
        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
          {result.scoreBreakdown.map(({ label, pts, max, tip }) => {
            const pct = max > 0 ? pts / max : 0;
            const barColor = pct >= 0.7 ? C.green : pct >= 0.4 ? C.yellow : C.red;
            return (
              <div key={label} title={tip}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: C.muted, marginBottom: 2 }}>
                  <span>{label}</span>
                  <span style={{ color: barColor, fontWeight: 700, fontFamily: "monospace" }}>
                    {Math.round(pts)}/{max}
                  </span>
                </div>
                <div style={{ height: 4, background: C.border + "40", borderRadius: 2, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${pct * 100}%`, background: barColor, borderRadius: 2, transition: "width 0.3s" }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Guidance — one actionable line */}
        {result.scoreGuidance && (
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.border}`, fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
            💡 {result.scoreGuidance}
          </div>
        )}
      </div>
    </div>
  );
}
